import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import SlideBase from './SlideBase'
import WrappedImageBackground from '../WrappedImageBackground'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import InteractiveZone from '../InteractiveZone'
import { WRAPPED_SLIDES } from '../../config/slides'
import useVideoBackgroundWithFallback from '../../hooks/useVideoBackgroundWithFallback'

const ButtonContainer = styled(InteractiveZone)`
  margin-top: ${rem(8)};
`

// Get slide config for this slide (static, doesn't depend on runtime data)
const SLIDE_CONFIG = WRAPPED_SLIDES.find(s => s.id === 'prediction')
const SLIDE_ID = 'prediction' as const

function PredictionSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  // Pre-fetched prediction data from useWrappedData hook - avoids flickering
  const predictionData = useWrappedStorySelector(state => state.predictionData)

  // Use the video background with fallback hook for when no content image is available
  const { background: videoBackground, isCurrentSlide } = useVideoBackgroundWithFallback({
    slideConfig: SLIDE_CONFIG,
    slideId: SLIDE_ID,
  })

  const activeBooking = data?.activeBooking

  const imageId = activeBooking?.imageId ?? predictionData?.imageId

  // Use content-specific image if available, otherwise use video background (with fallback)
  const background = useMemo(() => {
    if (imageId) {
      return <WrappedImageBackground imageId={imageId} kenBurnsVariant="zoom" isActive={isCurrentSlide && !isPaused} />
    }
    return videoBackground
  }, [imageId, isCurrentSlide, isPaused, videoBackground])

  const destinationName = predictionData?.destinationName
  const searchUrl = predictionData?.searchUrl ?? ''
  const offerCount = predictionData?.offerCount ?? 0
  const showSavings = (predictionData?.maxSavingsPercent ?? 0) > 0

  // Show active booking variant if user has one
  if (activeBooking) {
    return (
      <SlideBase background={background}>
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" gap={12} fullHeight>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">WHAT'S NEXT</Caption>
              <Heading variant="heading5"><b>Your dream {activeBooking.destination} trip is getting closer every day.</b></Heading>
            </Group>
            <Heading variant="heading1alt">{activeBooking.daysToGo} days to go!</Heading>
            <ButtonContainer>
              <TextButton
                fit="full-width"
                shape="round"
                kind="primary"
                target="_blank"
                to={`/trip-planner/generate-trip?orderId=${activeBooking.orderId}`}
              >
                Start planning your escape
              </TextButton>
            </ButtonContainer>
          </Group>
        </FadeBackground>
      </SlideBase>
    )
  } else if (predictionData) {
    return (
      <SlideBase background={background}>
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" gap={12} fullHeight>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">WHAT'S NEXT</Caption>
              <Heading variant="heading5"><b>This year, a trip to</b></Heading>
            </Group>
            <Group direction="vertical" gap={4}>
              <Heading variant="heading1alt">{destinationName} is calling!</Heading>
              <BodyText variant="medium">
                {offerCount || 'Many'} amazing offers{' '}
                {showSavings ? `with savings up to ${predictionData.maxSavingsPercent}%.` : 'available'}
              </BodyText>
            </Group>
            <ButtonContainer>
              <TextButton
                fit="full-width"
                shape="round"
                kind="primary"
                target="_blank"
                to={searchUrl}
              >
                Explore {destinationName}
              </TextButton>
            </ButtonContainer>
          </Group>
        </FadeBackground>
      </SlideBase>
    )
  } else {
    return (
      <SlideBase background={background}>
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" gap={12} fullHeight>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">WHAT'S NEXT</Caption>
              <Heading variant="heading5"><b>Ready for your next trip?</b></Heading>
            </Group>
            <Heading variant="heading1alt">Start planning your perfect escape for 2026</Heading>
            <ButtonContainer>
              <TextButton
                fit="full-width"
                shape="round"
                kind="primary"
                target="_blank"
                to="/search?destinationName=Anywhere&destinationId=le_d41d8cd98f00b204e9800998ecf8427e&adults=2&children=none&campaigns=Best+of+2025"
              >
                Browse Best of 2025
              </TextButton>
            </ButtonContainer>
          </Group>
        </FadeBackground>
      </SlideBase>
    )
  }
}

export default React.memo(PredictionSlide)
